/** Admin: manage inspectors and invite codes with the admin key. */
import { formatCode, type AdminUser, type AdminUserDetail, type CodeResponse } from '../../shared/api.ts';
import { INITIALS_RE } from '../../shared/doc.ts';
import { apiJson, describeError } from '../app/api.ts';
import { navigate } from '../app/router.ts';
import { getKV, setKV } from '../store/idb.ts';
import { h, icon, ICONS, toast, clear } from './dom.ts';
import { confirmSheet, openSheet, promptSheet } from './sheet.ts';

export function renderAdmin(root: HTMLElement): () => void {
  let key = '';
  const list = h('div', { class: 'list' });
  const status = h('p', { class: 'help' });
  const header = h('div', { class: 'page-head' }, h('button', { class: 'glass round', type: 'button', 'aria-label': 'Back', onClick: () => navigate({ name: 'library' }) }, icon(ICONS.back, 24)), h('h1', { class: 'large-title' }, 'Inspectors'), h('button', { class: 'glass', type: 'button', onClick: () => void changeKey() }, icon(ICONS.key, 20), 'Key'));
  const addBtn = h('button', { class: 'btn primary', type: 'button', onClick: () => void addUser() }, icon(ICONS.plus, 20), 'Add inspector');
  const body = h('div', { class: 'library' }, h('div', { class: 'stack' }, header, h('div', { class: 'bar' }, addBtn, status), list, h('p', { class: 'help' }, 'Each inspector has one personal sign-in code that works on any number of devices. Issue a new code only if the old one was shared; devices already signed in keep working.')));
  const screen = h('div', { class: 'screen' }, body);
  root.append(screen);

  const hdr = () => ({ 'x-admin-key': key });

  async function changeKey(): Promise<void> {
    const v = await promptSheet('Admin key', { value: key, label: 'Paste the admin key', mono: true, help: 'The key is stored only on this device.' });
    if (v === null) return;
    key = v.trim();
    await setKV('adminKey', key);
    await load();
  }
  async function load(): Promise<void> {
    if (!key) {
      status.textContent = 'Enter the admin key to continue.';
      clear(list);
      return;
    }
    try {
      status.textContent = 'Loading…';
      const users = await apiJson<AdminUser[]>('/api/admin/users', { headers: hdr() });
      status.textContent = `${users.length} inspector${users.length === 1 ? '' : 's'}`;
      renderUsers(users);
    } catch (e) {
      status.textContent = describeError(e);
      clear(list);
    }
  }
  function renderUsers(users: AdminUser[]): void {
    clear(list);
    if (!users.length) list.append(h('div', { class: 'item' }, h('div', { class: 'grow sub' }, 'No inspectors yet. Add the first one.')));
    for (const u of users) {
      list.append(
        h(
          'div',
          { class: 'item' },
          h('div', { class: 'grow' }, h('div', { class: 'name' }, `${u.name} · ${u.initials}`, u.disabledAt ? h('span', { class: 'sub' }, ' (disabled)') : null), h('div', { class: 'sub' }, `Added ${new Date(u.createdAt).toLocaleDateString()}`)),
          h('button', { class: 'btn subtle', type: 'button', onClick: () => void newCode(u) }, u.hasCode ? 'New code' : 'Sign-in code'),
          h('button', { class: 'btn icon', type: 'button', 'aria-label': 'Details', onClick: () => void details(u) }, icon(ICONS.more)),
        ),
      );
    }
  }
  async function addUser(): Promise<void> {
    const name = await promptSheet('New inspector', { label: 'Name', placeholder: 'Ricky Bunker', okLabel: 'Next' });
    if (!name?.trim()) return;
    const guess = name
      .trim()
      .split(/\s+/)
      .map((p) => p[0]!.toUpperCase())
      .join('')
      .slice(0, 3);
    const initials = await promptSheet('Initials', { label: 'Initials used in test IDs', value: guess, mono: true, okLabel: 'Create' });
    if (initials === null) return;
    const ini = initials.trim().toUpperCase();
    if (!INITIALS_RE.test(ini)) return toast('Initials must be 1–4 letters', 'error');
    try {
      const u = await apiJson<AdminUser>('/api/admin/users', { method: 'POST', headers: hdr(), body: JSON.stringify({ name: name.trim(), initials: ini }) });
      await load();
      await newCode(u, true);
    } catch (e) {
      toast(describeError(e), 'error');
    }
  }
  async function newCode(u: AdminUser, fresh = false): Promise<void> {
    if (!fresh && u.hasCode && !(await confirmSheet('Issue a new sign-in code?', `${u.name}'s current code stops working. Devices already signed in are not affected.`, 'New code', false))) return;
    try {
      const res = await apiJson<CodeResponse>(`/api/admin/users/${u.id}/code`, { method: 'POST', headers: hdr() });
      const code = formatCode(res.code);
      const link = `${location.origin}/signin?code=${res.code}`;
      const s = openSheet(`Sign-in code for ${u.name}`, { center: true });
      s.body.append(
        h('div', { class: 'input code readout' }, code),
        h('p', { class: 'help' }, 'This code works on every device they sign in on. It is shown once here; they can also see it in Settings after signing in.'),
        h('div', { class: 'chips' }, h('button', { class: 'chip', type: 'button', onClick: () => void navigator.clipboard?.writeText(code).then(() => toast('Code copied', 'success')) }, icon(ICONS.copy, 18), 'Copy code'), h('button', { class: 'chip', type: 'button', onClick: () => void navigator.clipboard?.writeText(link).then(() => toast('Link copied', 'success')) }, icon(ICONS.copy, 18), 'Copy link'), navigator.share ? h('button', { class: 'chip', type: 'button', onClick: () => void navigator.share({ text: `MapMark sign-in code: ${code}\n${link}` }).catch(() => {}) }, icon(ICONS.share, 18), 'Share') : null),
      );
      await load();
    } catch (e) {
      toast(describeError(e), 'error');
    }
  }
  async function details(u: AdminUser): Promise<void> {
    try {
      const d = await apiJson<AdminUserDetail>(`/api/admin/users/${u.id}`, { headers: hdr() });
      const s = openSheet(u.name);
      const mb = (d.usage.bytes / 1_048_576).toFixed(1);
      s.body.append(
        h('p', { class: 'help' }, `${d.usage.maps} maps · ${mb} MB stored · ${d.sessions.length} signed-in device${d.sessions.length === 1 ? '' : 's'}`),
        h('div', { class: 'list' }, ...d.sessions.map((sess) => h('div', { class: 'item' }, h('div', { class: 'grow' }, h('div', { class: 'name' }, sess.device), h('div', { class: 'sub' }, `Last used ${new Date(sess.lastSeenAt).toLocaleDateString()}`))))),
        h('div', { class: 'chips' },
          h('button', { class: 'chip', type: 'button', onClick: async () => { if (await confirmSheet('Sign out all devices?', `${u.name} can sign back in with their code.`, 'Sign out')) { await apiJson(`/api/admin/users/${u.id}/revoke`, { method: 'POST', headers: hdr() }); toast('Devices signed out', 'success'); s.close(); } } }, 'Sign out all devices'),
          h('button', { class: 'chip danger', type: 'button', onClick: async () => { if (await confirmSheet('Delete account?', `${u.name} and every map they stored will be erased permanently.`, 'Delete')) { await apiJson(`/api/admin/users/${u.id}`, { method: 'DELETE', headers: hdr() }); toast('Account deleted', 'success'); s.close(); await load(); } } }, 'Delete account'),
          h('button', { class: 'chip danger', type: 'button', onClick: async () => { const dis = !u.disabledAt; if (await confirmSheet(dis ? 'Disable account?' : 'Enable account?', dis ? 'They lose access immediately; their maps are kept.' : 'Their sign-in code works again.', dis ? 'Disable' : 'Enable', dis)) { await apiJson(`/api/admin/users/${u.id}/${dis ? 'disable' : 'enable'}`, { method: 'POST', headers: hdr() }); s.close(); await load(); } } }, u.disabledAt ? 'Enable account' : 'Disable account'),
        ),
      );
    } catch (e) {
      toast(describeError(e), 'error');
    }
  }
  void getKV<string>('adminKey').then((k) => {
    key = k ?? '';
    if (!key) void changeKey();
    else void load();
  });
  return () => screen.remove();
}
